/**
 * 递归过滤数组中 name 包含指定字符串的项
 * 返回新数组，不修改原数组（纯函数）
 * @param {Array} arr - 待过滤的数组
 * @param {string} str - 需要过滤掉的匹配字符串
 * @returns {Array} 过滤后的新数组
 */
export const filterData = (arr = [], str = '') => {
  return arr.reduce((acc, item) => {
    if (item.name && item.name.includes(str)) {
      return acc;
    }
    const newItem = { ...item };
    if (Array.isArray(newItem.childrenList)) {
      newItem.childrenList = filterData(newItem.childrenList, str);
    }
    acc.push(newItem);
    return acc;
  }, []);
};

// 默认需要合计的数值字段集合
const DEFAULT_SUM_KEYS = [
  'amount',
  'employmentAmount',
  'publicAmount',
  'specialAmount',
  'percent',
  'totalAmount',
  'totalPublicAmount',
  'totalEmployeeAmount',
  'totalPercent',
  'publicSpecialAmount',
  'employmentSpecialAmount'
];

/**
 * 对数组进行合计，将合计行 unshift 到数组开头
 * @param {Array} data - 待合计的数据数组
 * @param {Object} options - 配置项
 * @param {string[]} [options.sumKeys] - 需要合计的字段数组（默认使用预定义字段）
 * @param {boolean} [options.sumAllNumber=false] - 是否合计所有 number 类型字段（为 true 时忽略 sumKeys）
 * @param {Object|Function} [options.extraFields={}] - 合计行额外设置的字段 { key: value }，或接收 total 对象的函数
 * @returns {Array} 添加了合计行的数组（原地修改，unshift 到开头）
 */
export const summationRow = (data = [], options = {}) => {
  if (!data || data.length === 0) return data;

  const { sumKeys = DEFAULT_SUM_KEYS, sumAllNumber = false, extraFields = {} } = options;

  const keySet = sumAllNumber ? null : new Set(sumKeys);

  const total = data.reduce((acc, cur) => {
    for (const key in cur) {
      if (typeof acc[key] === 'undefined') {
        acc[key] = 0;
      }
      if (sumAllNumber) {
        if (typeof cur[key] === 'number') {
          acc[key] += cur[key];
        }
      } else if (keySet.has(key)) {
        acc[key] += cur[key];
      }
    }
    return acc;
  }, {});

  // 非合计字段置为空字符串
  if (!sumAllNumber) {
    for (const key in total) {
      if (total[key] === 0 && !keySet.has(key)) {
        total[key] = '';
      }
    }
  }

  // 设置合计行标识字段
  total.id = '0';
  total.name = '合计';

  // 应用额外字段配置（支持对象或函数）
  const fields = typeof extraFields === 'function' ? extraFields(total) : extraFields;
  Object.assign(total, fields);

  data.unshift(total);
  return data;
};

/**
 * 递归构建树形结构，设置 id、isClick、province 等字段
 * @param {Array} data - 待处理的数据数组
 * @param {Object} [options] - 配置项
 * @param {string} [options.idPrefix=''] - id 前缀（递归时使用）
 * @param {boolean} [options.setSublist=false] - 是否在省级设置 sublist 字段
 * @param {boolean} [options.startFromZero=false] - 是否从 0 开始编号（默认从 1 开始）
 * @returns {Array} 处理后的数据数组（原地修改）
 */
export const buildTree = (data = [], options = {}) => {
  const { idPrefix = '', setSublist = false, startFromZero = false } = options;

  if (!data || data.length === 0) return data;

  data.forEach((element, index) => {
    // 设置 id
    const baseIndex = startFromZero ? index : index + 1;
    element.id = idPrefix ? `${idPrefix}-${baseIndex}` : baseIndex;

    // 计算层级（通过 id 中 '-' 的数量）
    const level = String(element.id).split('-').length - 1;

    if (level === 0) {
      // 省级：设置子级的 province
      if (Array.isArray(element.childrenList)) {
        element.childrenList.forEach(child => {
          child.province = element.province;
        });
      }
      // 可选：设置 sublist
      if (setSublist) {
        element.sublist = element.childrenList;
      }
    }

    // 递归处理子级
    if (Array.isArray(element.childrenList) && element.childrenList.length > 0) {
      element.isClick = true;
      buildTree(element.childrenList, { idPrefix: element.id, setSublist, startFromZero });
    } else {
      element.childrenList = [];
      element.isClick = false;
    }
  });

  return data;
};



// WEBPACK FOOTER //
// ./src/utils/tool.js